import {
  Activity, AlignLeft, Apple, BookOpen, Briefcase, Building2, Calculator, Calendar, CalendarClock, Car,
  CarFront, ChefHat, ChevronsUpDown, CircleCheck, CircleDashed, CircleDotDashed, Code, Coffee, Cog,
  Croissant, Cpu, DollarSign, Dumbbell, Factory, Flower2, Gem, GraduationCap, Handshake, Hash, Heart,
  HeartPulse, Hospital, Hotel, Inbox, KeyRound, Lamp, Layers, Leaf, Lightbulb, Link2, ListChecks, Mail,
  Megaphone, MonitorPlay, Package, PartyPopper, Phone, Pill, Plane, Presentation, RadioTower,
  Refrigerator, Scale, Scissors, Server, Shapes, Shirt, ShoppingBag, ShoppingCart, Smartphone, Smile,
  Sofa, Sparkles, Stethoscope, Store, Tag, ToggleLeft, Truck, Tv, Type, Users, UtensilsCrossed, Wifi,
  Wrench, type LucideIcon,
} from 'lucide-react';
import type { IndustryStatus, TemplateEntityType, TemplateFieldKind } from '@/services/industry-configs.service';

export const INDUSTRY_ICONS: Record<string, LucideIcon> = {
  restaurant: UtensilsCrossed, cafe: Coffee, bakery: Croissant, catering: ChefHat,
  software: Code, it_services: Server, agency: Layers, digital_marketing: Megaphone,
  mobile_shop: Smartphone, mobile_repair: Wrench, electronics: Tv, appliances: Refrigerator,
  furniture: Sofa, home_decor: Lamp, fashion: Shirt,
  clothing_brand: Tag, cosmetics_brand: Gem, manufacturer: Factory,
  beauty_salon: Sparkles, spa: Flower2, barber_shop: Scissors,
  healthcare: HeartPulse, clinic: Stethoscope, hospital: Hospital, dental: Smile, pharmacy: Pill,
  real_estate: Building2, property_rental: KeyRound,
  education: GraduationCap, tutoring: BookOpen, coaching: Presentation, online_course: MonitorPlay,
  fitness: Activity, gym: Dumbbell, yoga_studio: Leaf,
  travel_agency: Plane, hotel: Hotel, car_rental: CarFront,
  ecommerce: ShoppingCart, retail: Store, grocery: Apple,
  professional_services: Briefcase, legal: Scale, accounting: Calculator, consulting: Lightbulb,
  automotive: Cog, car_dealership: Car,
  event_planning: PartyPopper, wedding: Heart,
  courier: Package, logistics: Truck,
  telecom: RadioTower, internet_provider: Wifi,
  other: Shapes,
};

export const GROUP_ICONS: Record<string, LucideIcon> = {
  'Food & Hospitality': UtensilsCrossed,
  Technology: Cpu,
  'Retail & Electronics': Smartphone,
  'Brands & Manufacturing': Factory,
  'Beauty & Wellness': Sparkles,
  'Health & Medical': Stethoscope,
  'Real Estate': Building2,
  Education: GraduationCap,
  'Fitness & Sport': Dumbbell,
  'Travel & Hospitality': Plane,
  Commerce: ShoppingBag,
  Professional: Briefcase,
  Automotive: Car,
  Events: PartyPopper,
  'Logistics & Delivery': Truck,
  Communications: RadioTower,
  Other: Shapes,
};

export const industryIcon = (key: string, group?: string): LucideIcon =>
  INDUSTRY_ICONS[key] ?? (group ? GROUP_ICONS[group] : undefined) ?? Shapes;

export const STATUS_ORDER: IndustryStatus[] = ['not_started', 'in_progress', 'done'];

export const STATUS_META: Record<IndustryStatus, { label: string; icon: LucideIcon; chip: string; dot: string }> = {
  not_started: { label: 'Not started', icon: CircleDashed, chip: 'bg-surface-alt text-text-muted', dot: 'bg-text-muted' },
  in_progress: { label: 'In progress', icon: CircleDotDashed, chip: 'bg-warning-soft text-warning', dot: 'bg-warning' },
  done: { label: 'Done', icon: CircleCheck, chip: 'bg-success-soft text-success', dot: 'bg-success' },
};

export const ENTITY_META: Record<TemplateEntityType, { label: string; singular: string; icon: LucideIcon }> = {
  lead: { label: 'Leads', singular: 'Lead', icon: Inbox },
  contact: { label: 'Contacts', singular: 'Contact', icon: Users },
  deal: { label: 'Deals', singular: 'Deal', icon: Handshake },
};
export const ENTITY_ORDER: TemplateEntityType[] = ['lead', 'contact', 'deal'];

export const KIND_META: Record<TemplateFieldKind, { label: string; icon: LucideIcon }> = {
  text: { label: 'Text', icon: Type },
  long_text: { label: 'Long text', icon: AlignLeft },
  number: { label: 'Number', icon: Hash },
  currency: { label: 'Currency', icon: DollarSign },
  date: { label: 'Date', icon: Calendar },
  datetime: { label: 'Date & time', icon: CalendarClock },
  dropdown: { label: 'Dropdown', icon: ChevronsUpDown },
  multi_select: { label: 'Multi-select', icon: ListChecks },
  boolean: { label: 'Yes / No', icon: ToggleLeft },
  url: { label: 'URL', icon: Link2 },
  email: { label: 'Email', icon: Mail },
  phone: { label: 'Phone', icon: Phone },
};
export const KIND_ORDER = Object.keys(KIND_META) as TemplateFieldKind[];

export const hasOptions = (kind: TemplateFieldKind) => kind === 'dropdown' || kind === 'multi_select';

export const slugifyKey = (s: string) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

export function timeAgo(iso?: string): string {
  if (!iso) return '—';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 30) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}
